import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { scrollToSection } from "./Reveal";
import { useLang, usePrefix, useT } from "@/i18n";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lang = useLang();
  const prefix = usePrefix();
  const t = useT().nav;

  const LINKS = [
    { hash: "#services", label: t.services },
    { hash: "#works", label: t.works },
    { hash: "#monferrato", label: t.monferrato },
    { hash: "#studio", label: t.studio },
    { hash: "#contact", label: t.contact },
  ];

  const homePath = prefix || "/";
  const galleryPath = `${prefix}/galleria`;
  const pathNoSlash = pathname.replace(/\/+$/, "") || "/";
  const onGallery = pathNoSlash === galleryPath;

  // href crawlabili (SEO): la navigazione resta SPA via onClick
  const homeHref = prefix ? `${prefix}/` : "/";
  const galleryHref = `${galleryPath}/`;
  const sectionHref = (hash) => `${homeHref}${hash}`;
  const enPath = pathNoSlash.replace(/^\/ru/, "") || "/";
  const enHref = enPath === "/" ? "/" : `${enPath}/`;
  const ruHref = enPath === "/" ? "/ru/" : `/ru${enPath}/`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const spa = (e, handler) => {
    e.preventDefault();
    handler();
  };

  const go = (hash) => {
    setOpen(false);
    if (pathNoSlash !== homePath) {
      navigate(homePath, { state: { scrollTo: hash } });
    } else {
      scrollToSection(hash);
    }
  };

  const goGallery = () => {
    setOpen(false);
    navigate(galleryPath);
  };

  const goHome = () => {
    setOpen(false);
    if (pathNoSlash !== homePath) navigate(homePath);
    else window.__lenis?.scrollTo(0, { duration: 1.4 });
  };

  const switchLang = (target) => {
    setOpen(false);
    if (target === lang) return;
    navigate(target === "ru" ? ruHref : enHref);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 backdrop-blur-xl transition-[background-color,border-color] duration-500 ${
        scrolled || onGallery ? "bg-bone/85 border-b border-ink/10" : "bg-bone/65 border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-4 lg:px-8 xl:px-12">
        <a href={homeHref} data-testid="nav-logo" onClick={(e) => spa(e, goHome)} className="flex shrink-0 items-center gap-2 text-left">
          <img src="/images/logo-mark.png" alt={t.brandAlt} className="h-9 w-auto xl:h-10" />
          <span className="flex flex-col gap-1.5">
            <span className="whitespace-nowrap text-[15px] font-semibold uppercase tracking-[0.22em] text-ink xl:text-[17px]">Monferrato Way</span>
            <span className="whitespace-nowrap text-[9.5px] uppercase tracking-[0.208em] text-ink/60 xl:text-[10px] xl:tracking-[0.264em]">Discover a different Italy</span>
          </span>
        </a>

        <nav className="hidden items-center gap-5 xl:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <a
              key={l.hash}
              href={sectionHref(l.hash)}
              data-testid={`nav-link-${l.hash.slice(1)}`}
              onClick={(e) => spa(e, () => go(l.hash))}
              className="whitespace-nowrap text-[11px] uppercase tracking-[0.18em] text-ink/70 transition-colors duration-300 hover:text-terra"
            >
              {l.label}
            </a>
          ))}
          <a
            href={galleryHref}
            data-testid="nav-link-gallery"
            onClick={(e) => spa(e, goGallery)}
            className={`whitespace-nowrap text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 hover:text-terra ${
              onGallery ? "text-terra" : "text-ink/70"
            }`}
          >
            {t.gallery}
          </a>
          <span className="flex items-center gap-1.5 border-l border-ink/15 pl-5" data-testid="lang-switcher">
            <a
              href={ruHref}
              data-testid="lang-ru"
              onClick={(e) => spa(e, () => switchLang("ru"))}
              className={`text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${lang === "ru" ? "text-terra" : "text-ink/40 hover:text-ink"}`}
            >
              RU
            </a>
            <span className="text-ink/25">/</span>
            <a
              href={enHref}
              data-testid="lang-en"
              onClick={(e) => spa(e, () => switchLang("en"))}
              className={`text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${lang === "en" ? "text-terra" : "text-ink/40 hover:text-ink"}`}
            >
              EN
            </a>
          </span>
          <a
            href={sectionHref("#contact")}
            data-testid="nav-cta-button"
            onClick={(e) => spa(e, () => go("#contact"))}
            className="whitespace-nowrap border border-ink px-4 py-2.5 text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 hover:bg-ink hover:text-bone"
          >
            {t.cta}
          </a>
        </nav>

        <button
          data-testid="nav-menu-toggle"
          className="xl:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-ink/10 bg-bone/95 px-6 py-8 backdrop-blur-xl xl:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-6">
            {LINKS.map((l) => (
              <a
                key={l.hash}
                href={sectionHref(l.hash)}
                data-testid={`nav-mobile-link-${l.hash.slice(1)}`}
                onClick={(e) => spa(e, () => go(l.hash))}
                className="text-left font-serif text-3xl tracking-tight"
              >
                {l.label}
              </a>
            ))}
            <a
              href={galleryHref}
              data-testid="nav-mobile-link-gallery"
              onClick={(e) => spa(e, goGallery)}
              className="text-left font-serif text-3xl tracking-tight"
            >
              {t.gallery}
            </a>
            <div className="flex gap-6 border-t border-ink/10 pt-6">
              <a
                href={ruHref}
                data-testid="lang-ru-mobile"
                onClick={(e) => spa(e, () => switchLang("ru"))}
                className={`text-sm uppercase tracking-[0.2em] ${lang === "ru" ? "text-terra" : "text-ink/50"}`}
              >
                Русский
              </a>
              <a
                href={enHref}
                data-testid="lang-en-mobile"
                onClick={(e) => spa(e, () => switchLang("en"))}
                className={`text-sm uppercase tracking-[0.2em] ${lang === "en" ? "text-terra" : "text-ink/50"}`}
              >
                English
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
